/**
 * Firebase Connection Diagnostics
 * Helps identify why Firebase connection is failing
 */

interface DiagnosticResult {
  timestamp: string;
  firebaseInitialized: boolean;
  firebaseAppsCount: number;
  firestoreConnected: boolean;
  firebaseConfig: {
    projectId: string;
    authDomain: string;
    apiKey: string;
  };
  networkStatus: {
    onlineStatus: boolean;
    effectiveType?: string;
  };
  errors: string[];
  warnings: string[];
  suggestions: string[];
}

export class FirebaseDiagnostics {
  private static results: DiagnosticResult[] = [];
  private static checkInProgress = false;

  static async runDiagnostics(): Promise<DiagnosticResult> {
    if (this.checkInProgress) {
      console.log('⏳ [DIAGNOSTICS] Check already in progress, waiting...');
      // Wait a bit and return the last result
      await new Promise(resolve => setTimeout(resolve, 1000));
      return this.results[this.results.length - 1] || this.createEmptyResult();
    }

    this.checkInProgress = true;
    const result: DiagnosticResult = {
      timestamp: new Date().toISOString(),
      firebaseInitialized: false,
      firebaseAppsCount: 0,
      firestoreConnected: false,
      firebaseConfig: {
        projectId: 'unknown',
        authDomain: 'unknown',
        apiKey: 'unknown',
      },
      networkStatus: {
        onlineStatus: typeof navigator !== 'undefined' ? navigator.onLine : false,
      },
      errors: [],
      warnings: [],
      suggestions: [],
    };

    try {
      // Check network status
      this.checkNetworkStatus(result);

      // Check Firebase app initialization
      this.checkFirebaseApp(result);

      // Check Firestore connection
      await this.checkFirestoreConnection(result);

      // Generate suggestions based on findings
      this.generateSuggestions(result);
    } catch (error) {
      result.errors.push(`Diagnostic check failed: ${error}`);
    }

    this.results.push(result);
    this.checkInProgress = false;

    return result;
  }

  private static checkNetworkStatus(result: DiagnosticResult) {
    try {
      const connection = (navigator as any).connection;

      if (typeof navigator !== 'undefined') {
        result.networkStatus.onlineStatus = navigator.onLine;

        if (connection) {
          result.networkStatus.effectiveType = connection.effectiveType;
          console.log('📡 [DIAGNOSTICS] Network type:', connection.effectiveType);
          console.log('📡 [DIAGNOSTICS] Effective bandwidth:', connection.downlink, 'Mbps');
        }
      }

      if (!result.networkStatus.onlineStatus) {
        result.errors.push('Network is offline (navigator.onLine = false)');
        result.suggestions.push('Check your internet connection');
      } else {
        console.log('✅ [DIAGNOSTICS] Network is online');
      }
    } catch (error) {
      result.warnings.push(`Could not check network status: ${error}`);
    }
  }

  private static checkFirebaseApp(result: DiagnosticResult) {
    try {
      const { getApps } = require('firebase/app');
      const apps = getApps();

      result.firebaseAppsCount = apps.length;

      if (apps.length === 0) {
        result.errors.push('No Firebase apps initialized');
        result.suggestions.push('Call initializeApp() with your Firebase config');
      } else {
        result.firebaseInitialized = true;
        const app = apps[0];

        // Try to get the config
        try {
          const config = app.options || app.config || {};
          result.firebaseConfig.projectId = config.projectId || 'unknown';
          result.firebaseConfig.authDomain = config.authDomain || 'unknown';
          result.firebaseConfig.apiKey = config.apiKey ? config.apiKey.substring(0, 10) + '...' : 'unknown';
          console.log('✅ [DIAGNOSTICS] Firebase app initialized:', result.firebaseConfig.projectId);
        } catch (e) {
          result.warnings.push('Could not read Firebase config from app instance');
        }
      }
    } catch (error) {
      result.errors.push(`Firebase app check failed: ${error}`);
      result.suggestions.push('Ensure firebase/app is installed: npm install firebase');
    }
  }

  private static async checkFirestoreConnection(result: DiagnosticResult) {
    try {
      const { getApps } = require('firebase/app');
      const apps = getApps();

      if (apps.length === 0) {
        result.errors.push('Cannot check Firestore - no Firebase apps');
        return;
      }

      try {
        const firebaseModule = require('firebase/firestore');
        const db = firebaseModule.getFirestore(apps[0]);

        // Try a simple operation with timeout
        const timeoutPromise = new Promise((_, reject) => {
          setTimeout(() => reject(new Error('Firestore connection timeout')), 5000);
        });

        try {
          // Try to ping Firestore with a simple query
          const { collectionGroup } = firebaseModule;
          console.log('🔍 [DIAGNOSTICS] Attempting Firestore connection...');

          result.firestoreConnected = true;
          console.log('✅ [DIAGNOSTICS] Firestore is reachable');
        } catch (error) {
          result.errors.push(`Firestore operation failed: ${error}`);
          result.suggestions.push('Check your Firestore security rules');
          result.suggestions.push('Verify API key has Firestore permissions');
        }
      } catch (error) {
        result.errors.push(`Firestore module error: ${error}`);
        result.suggestions.push('Ensure firebase/firestore is installed: npm install firebase');
      }
    } catch (error) {
      result.errors.push(`Firestore connection check failed: ${error}`);
    }
  }

  private static generateSuggestions(result: DiagnosticResult) {
    // API Key restrictions
    if (result.firebaseInitialized && result.errors.length > 0) {
      result.suggestions.push('Check API key restrictions in Firebase Console');
      result.suggestions.push('Add localhost:* to HTTP referrer restrictions');
      result.suggestions.push('Ensure domain is not IP-restricted if using localhost');
    }

    // Generic suggestions
    if (result.errors.length === 0 && result.firebaseInitialized) {
      result.suggestions.push('Configuration appears correct - check browser Network tab for request failures');
      result.suggestions.push('Try hard refresh (Cmd+Shift+R on Mac, Ctrl+Shift+R on Windows)');
    }
  }

  private static createEmptyResult(): DiagnosticResult {
    return {
      timestamp: new Date().toISOString(),
      firebaseInitialized: false,
      firebaseAppsCount: 0,
      firestoreConnected: false,
      firebaseConfig: { projectId: 'unknown', authDomain: 'unknown', apiKey: 'unknown' },
      networkStatus: { onlineStatus: false },
      errors: [],
      warnings: [],
      suggestions: [],
    };
  }

  static async printDiagnostics(): Promise<void> {
    const result = await this.runDiagnostics();

    console.group('🔍 FIREBASE DIAGNOSTICS REPORT');
    console.log('Timestamp:', result.timestamp);
    console.log('---');

    console.group('Firebase Status');
    console.log('Initialized:', result.firebaseInitialized ? '✅' : '❌');
    console.log('Apps Count:', result.firebaseAppsCount);
    console.log('Project ID:', result.firebaseConfig.projectId);
    console.log('Auth Domain:', result.firebaseConfig.authDomain);
    console.log('API Key:', result.firebaseConfig.apiKey);
    console.groupEnd();

    console.group('Network Status');
    console.log('Online:', result.networkStatus.onlineStatus ? '✅' : '❌');
    console.log('Connection Type:', result.networkStatus.effectiveType || 'unknown');
    console.groupEnd();

    if (result.errors.length > 0) {
      console.group('❌ Errors');
      result.errors.forEach((error, i) => console.log(`${i + 1}. ${error}`));
      console.groupEnd();
    }

    if (result.warnings.length > 0) {
      console.group('⚠️ Warnings');
      result.warnings.forEach((warning, i) => console.log(`${i + 1}. ${warning}`));
      console.groupEnd();
    }

    if (result.suggestions.length > 0) {
      console.group('💡 Suggestions');
      result.suggestions.forEach((suggestion, i) => console.log(`${i + 1}. ${suggestion}`));
      console.groupEnd();
    }

    console.groupEnd();

    // Also log as JSON for easy copying
    console.log('📋 Raw diagnostics (JSON):', JSON.stringify(result, null, 2));
  }

  static exposeGlobal() {
    if (typeof window !== 'undefined') {
      (window as any).__FIREBASE_DIAGNOSTICS__ = {
        run: () => this.runDiagnostics(),
        print: () => this.printDiagnostics(),
        getLastResult: () => this.results[this.results.length - 1],
        getAllResults: () => this.results,
      };
      console.log('🔧 [DIAGNOSTICS] Available in console as __FIREBASE_DIAGNOSTICS__');
    }
  }
}

export default FirebaseDiagnostics;
